import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Button, Card, Field, Input, MessageBar, MessageBarBody, Text, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { MIN_PASSWORD_LENGTH } from '@vxh/shared';
import { api } from '../api';
import { PageHeader } from '../components/PageHeader';

const useStyles = makeStyles({
  card: { maxWidth: '440px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', ...shorthands.gap('16px'), ...shorthands.padding('24px') },
  wrap: { minHeight: '100vh', display: 'grid', placeItems: 'center', ...shorthands.padding('24px'), backgroundColor: tokens.colorNeutralBackground2 },
});

export function ChangePasswordPage({ forced = false }: { forced?: boolean }) {
  const s = useStyles();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const change = useMutation({
    mutationFn: () => api('/auth/password', { method: 'POST', body: { current, next } }),
    onSuccess: async () => {
      setError(null);
      setSaved(true);
      setCurrent('');
      setNext('');
      setAgain('');
      await qc.invalidateQueries({ queryKey: ['auth'] });
      if (!forced) navigate('/');
    },
    onError: (e) => setError((e as Error).message),
  });
  const mismatch = again.length > 0 && again !== next;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (next.length >= MIN_PASSWORD_LENGTH && next === again) change.mutate();
  };

  const form = (
    <Card className={s.card}>
      {forced && <Text>You are using a temporary password. Choose your own to continue.</Text>}
      <form onSubmit={submit} style={{ display: 'grid', gap: 16 }}>
        <Field label={forced ? 'Temporary password' : 'Current password'}>
          <Input type="password" value={current} onChange={(_, d) => setCurrent(d.value)} autoComplete="current-password" />
        </Field>
        <Field label="New password" hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}>
          <Input type="password" value={next} onChange={(_, d) => setNext(d.value)} autoComplete="new-password" />
        </Field>
        <Field label="New password again" validationState={mismatch ? 'error' : 'none'} validationMessage={mismatch ? 'The two do not match.' : undefined}>
          <Input type="password" value={again} onChange={(_, d) => setAgain(d.value)} autoComplete="new-password" />
        </Field>
        {error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}
        {saved && !forced && (
          <MessageBar intent="success">
            <MessageBarBody>Password changed.</MessageBarBody>
          </MessageBar>
        )}
        <Button appearance="primary" type="submit" disabled={change.isPending || !current || next.length < MIN_PASSWORD_LENGTH || next !== again}>
          Change password
        </Button>
      </form>
    </Card>
  );
  if (forced) return <div className={s.wrap}>{form}</div>;
  return (
    <>
      <PageHeader title="Change password" />
      {form}
    </>
  );
}
